// @ts-nocheck
import React, { useMemo, useState } from "react";
import {
  coinSymbol,
  resolveCoinIconUrl,
  localSymbolFallbackSrc,
  resolveWithdrawHistoryNetworkForCoin,
} from "./withdrawPageHelpers";

// Demo display matching address-book header (screenshot)
const WITHDRAW_DEMO_UID = "1234567876554";
// Demo: internal transfer confirmation (AGCX User tab)
const AGCE_TRANSFER_DEMO = {
  methodLabel: "AGCX ID",
  recipient: "10612342995",
  sourceWallet: "Spot Wallet / Funding Wallet",
  note: "Hi, this transfer is from AGCX. Enjoy!",
};

function resolveTxExplorerUrl(network, txid) {
  const n = (network || "").toLowerCase();
  if (n.includes("bsc") || n.includes("bep")) return `https://bscscan.com/tx/${txid}`;
  if (n.includes("trc") || n.includes("tron")) return `https://tronscan.org/#/transaction/${txid}`;
  return `https://etherscan.io/tx/${txid}`;
}

function resolveAddressExplorerUrl(network, address) {
  const n = (network || "").toLowerCase();
  if (n.includes("bsc") || n.includes("bep")) return `https://bscscan.com/address/${address}`;
  if (n.includes("trc") || n.includes("tron")) return `https://tronscan.org/#/address/${address}`;
  return `https://etherscan.io/address/${address}`;
}

const WithdrawHistCopyIcon = () => (
  <svg className="withdraw_hist_icon_svg" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9 3h11v13h-3V6H9V3zM4 8v13h11V8.02L4 8z"
      fill="currentColor"
    />
  </svg>
);

const WithdrawHistExternalIcon = () => (
  <svg className="withdraw_hist_icon_svg" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5M16.5 3H21m0 0v4.5M21 3 9 15"
    />
  </svg>
);

function withdrawStatusTone(label) {
  const t = String(label || "").toLowerCase();
  if (!t || t === "—") return "neutral";
  if (/(completed|confirmed|success|succeed|done)/i.test(t)) return "success";
  if (/(pending|processing|confirming|in progress|queued|await|submitted|review|approval)/i.test(t)) return "pending";
  if (/(rejected|reject|failed|fail|cancel|canceled|error|expired)/i.test(t)) return "danger";
  return "info";
}

// Copy / explorer controls inside a row should not open the detail modal.
function withdrawHistoryClickShouldIgnoreModal(target) {
  if (!target || typeof target.closest !== "function") return false;
  return Boolean(target.closest("button, a[href]"));
}

// Stable key for “already validated this address on this network + token asset”.
function withdrawalAddressValidationKey(net, addr, tokenAssetId) {
  const a = String(addr || "").trim();
  const n = net != null && String(net).trim() && String(net) !== "Select Network" ? String(net).trim() : "";
  const t = tokenAssetId != null && String(tokenAssetId).trim() ? String(tokenAssetId).trim() : "";
  if (!n || !a) return "";
  return `${n}::${a}::${t}`;
}

function formatWhitelistExpiry(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return String(iso);
  }
}

function formatWithdrawAddressHistoryDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
  } catch {
    return String(iso);
  }
}

// How long to wait for account access + personal_sign before clearing UI.
const METAMASK_SIGN_FLOW_TIMEOUT_MS = 60_000;

async function promiseWithTimeout(promise, ms, timeoutErrorMessage) {
  let timer;
  try {
    return await Promise.race([
      promise,
      new Promise((_, reject) => {
        timer = window.setTimeout(() => reject(new Error(timeoutErrorMessage)), ms);
      }),
    ]);
  } finally {
    if (timer != null) window.clearTimeout(timer);
  }
}

// EIP-1193 provider for signing; when several wallets inject ethereum.providers, prefer MetaMask.
function getEip1193ProviderForSigning() {
  if (typeof window === "undefined") return null;
  const eth = window.ethereum;
  if (!eth) return null;

  const list = eth.providers;
  if (Array.isArray(list) && list.length > 0) {
    const mm = list.find((p) => p && p.isMetaMask && typeof p.request === "function");
    if (mm) return mm;
    const withReq = list.find((p) => p && typeof p.request === "function");
    if (withReq) return withReq;
  }

  if (typeof eth.request === "function") return eth;
  return null;
}

// EIP-191 personal_sign via injected wallet (MetaMask-compatible).
async function signChallengeWithMetaMask(challenge) {
  const eth = getEip1193ProviderForSigning();
  if (!eth) throw new Error("MetaMask or a compatible wallet is not available.");
  try {
    await eth.request({ method: "eth_requestAccounts" });
    const accounts = await eth.request({ method: "eth_accounts" });
    const account = accounts?.[0];
    if (!account) throw new Error("No wallet account connected.");
    const utf8Hex =
      "0x" +
      Array.from(new TextEncoder().encode(challenge))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
    const signature = await eth.request({
      method: "personal_sign",
      params: [utf8Hex, account],
    });
    return signature;
  } catch (e) {
    const code = e?.code;
    const raw = String(e?.message ?? e ?? "");
    if (code === 4001 || /user rejected|denied the request/i.test(raw)) {
      throw new Error("Wallet request was cancelled. Approve the prompt when you try again.");
    }
    if (/extension not found|failed to connect/i.test(raw)) {
      throw new Error(
        "Could not reach the wallet extension. Reload the page, unlock MetaMask, then try again."
      );
    }
    throw new Error("Could not complete wallet signing. Unlock the wallet and allow this site.");
  }
}

// Official download page (browser extension + mobile).
const METAMASK_DOWNLOAD_URL = "https://metamask.io/download/";

// Detect MetaMask (or compatible) injection for the Sign-with-MetaMask whitelist step.
function hasMetaMaskExtension() {
  if (typeof window === "undefined") return false;
  const eth = window.ethereum;
  if (!eth) return false;
  if (eth.isMetaMask === true && typeof eth.request === "function") return true;
  const list = eth.providers;
  if (Array.isArray(list) && list.length > 0) {
    return list.some((p) => p && p.isMetaMask === true && typeof p.request === "function");
  }
  return false;
}

// True when confirm-satoshi means deposit not verified yet (not terminal). Extend if API codes differ.
function satoshiConfirmIndicatesProofStillPending(res) {
  if (!res) return true;
  if (res.success === false) {
    const code = String(res.code || "")
      .toUpperCase()
      .replace(/-/g, "_");
    if (code === "PROOF_EXPIRED") return false;
    if (
      code === "PROOF_PENDING" ||
      code === "PENDING" ||
      code === "NOT_FOUND" ||
      code === "NOT_DETECTED" ||
      code === "NO_PROOF" ||
      code === "DEPOSIT_NOT_RECEIVED" ||
      code === "AWAITING_DEPOSIT" ||
      code.includes("PENDING")
    ) {
      return true;
    }
    const msg = String(res.message || "").toLowerCase();
    if (
      msg.includes("not found") ||
      msg.includes("not detected") ||
      msg.includes("not yet") ||
      msg.includes("still waiting") ||
      msg.includes("no deposit") ||
      msg.includes("awaiting")
    ) {
      return true;
    }
    return false;
  }
  const st = String(res.data?.status || "")
    .toUpperCase()
    .replace(/-/g, "_");
  if (st === "APPROVED" || st === "REJECTED" || st === "EXPIRED" || st === "PROOF_EXPIRED") return false;
  if (!st) return true;
  if (st.startsWith("PENDING") || st === "PROCESSING" || st === "SUBMITTED" || st === "CONFIRMING") {
    return true;
  }
  return true;
}

function addressBookEntryWithdrawable(entry) {
  const s = entry.status != null ? String(entry.status).toUpperCase() : "";
  return !s || s === "APPROVED";
}

// User started MetaMask whitelist before signing; can resume with stored challenge until expiry.
function addressBookEntryPendingMetaMaskSignature(entry) {
  const st =
    entry.status != null ? String(entry.status).toUpperCase().replace(/-/g, "_").trim() : "";
  if (st !== "PENDING_SIGNATURE") return false;
  const method = String(entry.whitelist_method ?? entry.whitelistMethod ?? "").toUpperCase();
  if (method !== "METAMASK") return false;
  const challenge = entry.metamask_challenge ?? entry.metamaskChallenge ?? "";
  const eid = entry._id ?? entry.id;
  return Boolean(eid && String(challenge).trim());
}

function addressBookMetaMaskChallengeExpiryIso(entry) {
  const raw = entry.metamask_expires_at ?? entry.metamaskExpiresAt ?? null;
  return raw != null && String(raw).trim() ? String(raw).trim() : "";
}

// Satoshi / micro-deposit whitelist not completed yet (server has proof amount; user can reopen QR + deposit address).
function addressBookEntryPendingSatoshiDeposit(entry) {
  if (addressBookEntryPendingMetaMaskSignature(entry)) return false;
  const st =
    entry.status != null ? String(entry.status).toUpperCase().replace(/-/g, "_").trim() : "";
  if (!st || st === "APPROVED" || st === "REJECTED" || st === "EXPIRED") return false;
  const method = String(entry.whitelist_method ?? entry.whitelistMethod ?? "").toUpperCase();
  if (method === "METAMASK") return false;

  const eid = entry._id ?? entry.id;
  if (!eid) return false;

  const amt = entry.satoshi_amount ?? entry.proof_amount ?? entry.satoshiAmount ?? entry.proofAmount;
  if (amt == null || String(amt).trim() === "") return false;

  return true;
}

// Resolve Fireblocks tokenAssetId for address-book row (getAndGenerateAddress).
function resolveAddressBookTokenAssetId(entry, withdrawCoins) {
  const direct = String(entry.token_asset_id ?? entry.tokenAssetId ?? "").trim();
  if (direct) return direct;
  const rowCoin = String(entry.coin ?? entry.short_name ?? "").trim().toUpperCase();
  if (!rowCoin || !Array.isArray(withdrawCoins) || withdrawCoins.length === 0) return "";
  const coinObj = withdrawCoins.find((c) => coinSymbol(c).toUpperCase() === rowCoin) || null;
  if (!coinObj) return "";
  const chainHint =
    entry.chain ?? entry.satoshi_chain ?? entry.proof_chain ?? entry.network ?? "";
  const netCode = resolveWithdrawHistoryNetworkForCoin(
    { fireblocks_asset_id: direct, chain: chainHint },
    coinObj
  );
  if (netCode && coinObj.token_asset_ids?.[netCode] != null) {
    return String(coinObj.token_asset_ids[netCode]).trim();
  }
  return "";
}

function addressBookStatusBadgeLabel(entry) {
  const s = entry.status != null ? String(entry.status).toUpperCase().replace(/_/g, " ") : "";
  if (!s) return null;
  return s;
}

function addressBookDeleteConfirmTitle(c) {
  if (!c) return "Remove address?";
  const st = c.statusUpper || "";
  if (st === "APPROVED") return "Remove whitelisted address?";
  if (st.startsWith("PENDING")) return "Remove pending address?";
  return "Remove saved address?";
}

function addressBookDeleteConfirmMessage(c) {
  if (!c) return "";
  const { label, shortAddr, statusUpper, chain, coin } = c;
  const parts = [coin || null, chain || null].filter(Boolean);
  const meta = parts.length ? parts.join(" · ") : "";
  const head = meta ? `${label} — ${meta} — ${shortAddr}` : `${label} (${shortAddr})`;
  const st = statusUpper || "";
  if (st === "APPROVED") {
    return `You are about to remove ${head}. You will need to verify this address again before you can withdraw to it.`;
  }
  if (st.startsWith("PENDING")) {
    return `You are about to remove ${head}. Any in-progress verification will be cancelled.`;
  }
  if (st === "REJECTED" || st === "EXPIRED") {
    return `Remove ${head} from your address book?`;
  }
  if (st) {
    return `Remove ${head}? Current status: ${st.replace(/_/g, " ")}.`;
  }
  return `Remove ${head} from your saved addresses?`;
}

// Bootstrap-style ring (same as Trade orderbook empty-state) for withdraw submit / verify buttons
const WITHDRAW_BTN_SPINNER_STYLE = {
  width: "1.5rem",
  height: "1.5rem",
  borderColor: "rgba(0, 0, 0, 0.3)",
  borderRightColor: "transparent",
};

function WithdrawButtonSpinner() {
  return (
    <span
      className="spinner-border spinner-border-sm me-2 align-middle d-inline-block"
      role="status"
      style={WITHDRAW_BTN_SPINNER_STYLE}
    >
      <span className="visually-hidden">Loading</span>
    </span>
  );
}

// Compact spinner for “Send code” in OTP rows
function WithdrawOtpSendSpinner() {
  return (
    <span
      className="spinner-border spinner-border-sm me-1 align-middle d-inline-block"
      role="status"
      style={{
        width: "0.85rem",
        height: "0.85rem",
        borderWidth: "0.15em",
        borderColor: "rgba(0, 0, 0, 0.35)",
        borderRightColor: "transparent",
      }}
    >
      <span className="visually-hidden">Loading</span>
    </span>
  );
}

const WITHDRAW_OTP_RESEND_COOLDOWN_S = 60;
// Email / SMS OTP and authenticator (TOTP) digits allowed in withdraw verification UI
const WITHDRAW_VERIFY_CODE_LEN = 6;

// Withdraw amount field: digits only plus optional single decimal point (no minus or other characters).
function sanitizeWithdrawAmountRaw(raw) {
  let v = String(raw ?? "").replace(/[^\d.]/g, "");
  const firstDot = v.indexOf(".");
  if (firstDot !== -1) {
    v = v.slice(0, firstDot + 1) + v.slice(firstDot + 1).replace(/\./g, "");
  }
  return v;
}

const SAVE_ADDR_COIN_OPTIONS = [
  { value: "USDT", label: "USDT" },
  { value: "BTC", label: "BTC" },
  { value: "ETH", label: "ETH" },
];

const SAVE_ADDR_NETWORK_OPTIONS = [
  { value: "bep20", label: "BNB Smart Chain (BEP20)" },
  { value: "erc20", label: "Ethereum (ERC20)" },
  { value: "trc20", label: "TRON (TRC20)" },
];

/** Add-address modal wizard steps */
const SAVE_ADDR_WIZARD_STEP = {
  BASICS: "basics",
  OWNER: "owner",
  SELF_WALLET_TYPE: "self_wallet_type",
  SELF_EXCHANGE: "self_exchange",
  SELF_VERIFY: "self_verify",
  /** Account 2FA before `address-book/initiate` (Satoshi/MetaMask is separate ownership proof). */
  VERIFY_ACCOUNT: "verify_account",
  OTHER_IDENTITY: "other_identity",
  OTHER_WALLET_TYPE: "other_wallet_type",
  OTHER_EXCHANGE: "other_exchange",
};

/** Stub list until withdrawal API supplies VASP names */
const ADDRESS_BOOK_TOP_EXCHANGES = [
  { value: "BINANCE", label: "Binance" },
  { value: "COINBASE", label: "Coinbase" },
  { value: "KRAKEN", label: "Kraken" },
  { value: "OKX", label: "OKX" },
  { value: "BYBIT", label: "Bybit" },
  { value: "KUCOIN", label: "KuCoin" },
  { value: "BITFINEX", label: "Bitfinex" },
  { value: "CRYPTOCOM", label: "Crypto.com" },
];

/** Sentinel value when the user selects "Other" and types the institution name manually */
const ADDRESS_BOOK_EXCHANGE_OTHER = "__OTHER__";

const ADDRESS_BOOK_DECLARATION_TEXT =
  "I declare that the information I have provided is true, accurate, and complete and that my transaction complies with AGCX Terms of Use.";

// Withdraw “Select Coin” UI (static list; must match modal + quick-pick rows)
const WITHDRAW_UI_COIN_META = {
  USDT: { name: "Tether", icon: "/images/tether_icon.png" },
  BTC: { name: "Bitcoin", icon: "/images/bitcoin_icon.png" },
  ETH: { name: "Ethereum", icon: "/images/eth_currency_img.png" },
  BNB: { name: "BNB", icon: "/images/AnnouncementImg/bnb_icon.svg" },
};

const SELECT_COIN_PLACEHOLDER = "Select coin";

const TRENDING_COIN_SYMBOLS = [
  "BTC",
  "ETH",
  "BNB",
  "USDT",
  "USDC",
  "XRP",
  "SOL",
  "DOGE",
  "MATIC",
  "DOT",
  "LTC",
  "TRX",
  "SHIB",
  "ADA",
  "BUSD",
];

const StepBadge = ({ step, done }) => {
  if (done) {
    return (
      <span className="deposit_step_badge is-done" aria-label={`Step ${step} completed`}>
        <img src="/images/selectcheck.svg" alt="" aria-hidden="true" width="14" height="14" />
      </span>
    );
  }

  return (
    <span className="deposit_step_badge" aria-label={`Step ${step}`}>
      {step}
    </span>
  );
};

function WithdrawCoinThumb({ coin, size = 30 }) {
  const [idx, setIdx] = useState(0);
  const sym = (coinSymbol(coin) || "?").toString().trim().toUpperCase();
  const avatarSrc = useMemo(() => {
    const t = (sym && sym !== "—" ? sym : "?").slice(0, 4);
    // Deterministic-ish color from symbol
    let hash = 0;
    for (let i = 0; i < t.length; i += 1) hash = (hash * 31 + t.charCodeAt(i)) | 0;
    const hue = Math.abs(hash) % 360;
    const bg = `hsl(${hue} 70% 42%)`;
    const fg = "#ffffff";
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}"><rect width="${size}" height="${size}" rx="${Math.floor(
      size / 2
    )}" fill="${bg}"/><text x="50%" y="50%" dominant-baseline="central" text-anchor="middle" fill="${fg}" font-family="Inter,system-ui,-apple-system,Segoe UI,Roboto,Arial" font-size="${Math.max(
      10,
      Math.floor(size * 0.36)
    )}" font-weight="800" letter-spacing="0.5">${t.slice(0, 2)}</text></svg>`;
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  }, [sym, size]);

  const candidates = useMemo(() => {
    const list = [];
    const api = resolveCoinIconUrl(coin);
    if (api) list.push(api);
    const local = localSymbolFallbackSrc(coin);
    // If the helper falls back to tether for unknown symbols, avoid showing the same icon everywhere.
    if (local && (!local.includes("tether_icon") || sym === "USDT")) list.push(local);
    return list;
  }, [coin, sym]);

  const src = candidates[idx];
  return (
    <img
      className="withdraw-coin-thumb__img"
      src={src || avatarSrc}
      alt=""
      width={size}
      height={size}
      loading="lazy"
      decoding="async"
      onError={() => {
        if (idx < candidates.length - 1) setIdx((v) => v + 1);
        else setIdx(candidates.length); // forces avatar
      }}
    />
  );
}

export {
  WITHDRAW_DEMO_UID,
  AGCE_TRANSFER_DEMO,
  resolveTxExplorerUrl,
  resolveAddressExplorerUrl,
  WithdrawHistCopyIcon,
  WithdrawHistExternalIcon,
  withdrawStatusTone,
  withdrawHistoryClickShouldIgnoreModal,
  withdrawalAddressValidationKey,
  formatWhitelistExpiry,
  formatWithdrawAddressHistoryDate,
  METAMASK_SIGN_FLOW_TIMEOUT_MS,
  METAMASK_DOWNLOAD_URL,
  promiseWithTimeout,
  getEip1193ProviderForSigning,
  signChallengeWithMetaMask,
  hasMetaMaskExtension,
  satoshiConfirmIndicatesProofStillPending,
  addressBookEntryWithdrawable,
  addressBookEntryPendingMetaMaskSignature,
  addressBookMetaMaskChallengeExpiryIso,
  addressBookEntryPendingSatoshiDeposit,
  resolveAddressBookTokenAssetId,
  addressBookStatusBadgeLabel,
  addressBookDeleteConfirmTitle,
  addressBookDeleteConfirmMessage,
  WITHDRAW_BTN_SPINNER_STYLE,
  WithdrawButtonSpinner,
  WithdrawOtpSendSpinner,
  WITHDRAW_OTP_RESEND_COOLDOWN_S,
  WITHDRAW_VERIFY_CODE_LEN,
  sanitizeWithdrawAmountRaw,
  SAVE_ADDR_COIN_OPTIONS,
  SAVE_ADDR_NETWORK_OPTIONS,
  SAVE_ADDR_WIZARD_STEP,
  ADDRESS_BOOK_TOP_EXCHANGES,
  ADDRESS_BOOK_EXCHANGE_OTHER,
  ADDRESS_BOOK_DECLARATION_TEXT,
  WITHDRAW_UI_COIN_META,
  SELECT_COIN_PLACEHOLDER,
  TRENDING_COIN_SYMBOLS,
  StepBadge,
  WithdrawCoinThumb,
};
